import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { SurfaceError } from '@shared/ui/SurfaceError';

import { PeopleSection } from '../_shared/components';
import { PEOPLE_ROUTES } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import { peopleErrors } from '../_shared/errors';
import { creditedItemsKey, creditProfileKey, useCreditedItems, useCreditProfile } from '../_shared/queries';

import { copy } from './copy';
import { PersonDetailCredits } from './PersonDetail.Credits';
import { PersonDetailForm } from './PersonDetail.Form';
import { PersonDetailHead } from './PersonDetail.Head';
import { PersonDetailSkeleton } from './PersonDetail.Skeleton';

/**
 * `/people/:profileSlug` — one credited person.
 *
 * **The route keys on the slug, and that is a contract consequence rather than
 * a preference.** `backend-content` publishes no `GET /admin/creditProfiles/{id}`
 * — its by-id read exists but only its API-key-gated internal surface calls it —
 * while `GET /creditHolders/{slug}` serves the same record from the same table
 * through the same service. The pack already calls the slug this record's only
 * identifier, and the **id** every write needs arrives inside that payload.
 *
 * **Three breadcrumb levels, not two.** Only real routes are crumbs, and
 * `/people` is one — it is the register, not a bucket name.
 *
 * **No delete for the person.** The catalog names create, edit and maintain —
 * no destructive verb — so the action is not drawn even though the service
 * publishes the route.
 *
 * **The works table is read-only, and that is the whole shape of this page
 * now.** A credit answers «what did this person do *on that title*», so it is
 * authored where the title is authored — the credits section of
 * `/content/:id/edit`, which already draws the register beside a role vocabulary
 * and toggles a record per role. Editing the same relation from two surfaces
 * meant an operator adding a cast member had to leave the title they were
 * authoring, find the person, and attach the title back to them by name. The
 * table stays because the question it answers — «what is this person already
 * on?» — is a real one and has no other surface; every row still opens its
 * title, which is where the relation is changed.
 */
export function PersonDetailPage() {
  const { profileSlug = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const profile = useCreditProfile(profileSlug);
  // The credits read is keyed on the profile ID, not the slug: the operator
  // route that shows unpublished credits takes the id, and the id arrives inside
  // the head payload above. Until it does the query stays disabled rather than
  // firing against an empty path.
  const profileId = profile.data?.id ?? '';
  const credits = useCreditedItems(profileId);

  const person = profile.data;
  const items = credits.data?.items ?? [];

  useEffect(() => {
    if (person) document.title = copy.documentTitle(person.display_name);
  }, [person]);

  if (profile.isError) {
    return (
      <>
        <Breadcrumb
          items={[
            { label: sharedCopy.crumbs.console, to: '/' },
            { label: sharedCopy.crumbs.register, to: PEOPLE_ROUTES.register },
          ]}
        />
        <div className="mt-5">
          <SurfaceError
            error={profile.error}
            catalog={peopleErrors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: creditProfileKey(profileSlug) })}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/*
        The third crumb appears with the name it states. While the record is in
        flight there is no third level to name — and repeating the register's
        own label there would announce a location the reader is not at, on top
        of putting the same label in the trail twice.
      */}
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.register, to: PEOPLE_ROUTES.register },
          ...(person ? [{ label: person.display_name }] : []),
        ]}
      />

      {profile.isPending ? <PersonDetailSkeleton /> : null}

      {person ? (
        <>
          <PersonDetailHead
            person={person}
            credits={items}
            creditsReady={credits.isSuccess}
            emptyRoles={sharedCopy.noRoles}
          />

          <PeopleSection
            id="person-fields-heading"
            heading={copy.fields.heading}
            context={copy.fields.context}
          >
            <PersonDetailForm person={person} />
          </PeopleSection>

          <PeopleSection
            id="person-credits-heading"
            heading={copy.credits.heading}
            context={copy.credits.context}
          >
            {credits.isError ? (
              <SurfaceError
                error={credits.error}
                catalog={peopleErrors}
                onRetry={() => void queryClient.invalidateQueries({ queryKey: creditedItemsKey(profileId) })}
              />
            ) : (
              <PersonDetailCredits
                items={items}
                pending={credits.isPending}
                hasMore={credits.data?.has_next ?? false}
                personName={person.display_name}
                onOpenTitle={(row) => void navigate(`/content/${row.id}/edit`)}
              />
            )}
          </PeopleSection>
        </>
      ) : null}
    </>
  );
}
